<script lang="tsx">
import { type Moment } from 'moment'
import {
  defineComponent,
  inject,
  ref, watch,
  type PropType, type Ref,
} from 'vue'
import { DefaultButton, ModalWindow, type ModalWindowInterface } from '../../../../lib'
import PeriodCalendar from '../../../calendars/calendars/period-calendar/period-calendar'
import PeriodCalendarInput from '../../../calendars/inputs/period/period-calendar-input'
import { type ComputedColumnInterface } from '../../DefaultTableInterfaces'
import { PeriodCalendarTableFilter } from '../../filters/Filters'
import HeaderSort from './DefaultTableHeaderSort.vue'

export default defineComponent({
  props: {
    column: {
      type: Object as PropType<ComputedColumnInterface>,
      required: true,
    },
    sortField: {
      type: String,
      required: true,
    },
    enableSorting: {
      type: Boolean,
    },
    sortOrder: {
      type: Boolean as PropType<boolean>,
      default: undefined,
    },
    onSort: {
      type: Function as PropType<() => void>,
      default: undefined,
    },
  },
  setup(props) {
    const modal = ref<ModalWindowInterface|undefined>()
    const isUseModalFilters: Ref<boolean> | undefined = inject('isUseModalFilters')

    const startDate: Ref<Moment | undefined> = ref(undefined)
    const endDate: Ref<Moment | undefined> = ref(undefined)
    const filter: PeriodCalendarTableFilter = props.column.filter as unknown as PeriodCalendarTableFilter

    const modalStartDate: Ref<Moment | undefined> = ref(undefined)
    const modalEndDate: Ref<Moment | undefined> = ref(undefined)

    const setIsModalOpen = (value: boolean) => {
      if (!isUseModalFilters?.value) return

      if (modal.value) {
        if (value) modal.value.open()
        else modal.value.close()
      }
    }

    const setValue = (val: Moment | undefined, isEndVal?: boolean) => {
      startDate.value = !isEndVal ? val : startDate.value
      endDate.value = isEndVal ? val : endDate.value

      if (filter.isSetOnlyFullDate && !isEndVal && startDate.value) {
        endDate.value = undefined
      }

      // для связывания значений при изменении масштаба
      modalStartDate.value = startDate.value
      modalEndDate.value = endDate.value

      if (filter.isSetOnlyFullDate && startDate.value && endDate.value) {
        filter.addValue({
          endDate: endDate.value,
          startDate: startDate.value,
        })
      } else if (!startDate.value && !endDate.value) {
        filter.removeValues()
      } else if (!filter.isSetOnlyFullDate) {
        filter.addValue({
          endDate: endDate.value,
          startDate: startDate.value,
        })
      }
    }

    const setModalValue = (val: Moment | undefined, isEndVal?: boolean) => {
      modalStartDate.value = !isEndVal ? val : modalStartDate.value
      modalEndDate.value = isEndVal ? val : modalEndDate.value

      if (filter.isSetOnlyFullDate && !isEndVal && modalStartDate.value) {
        modalEndDate.value = undefined
      }
    }

    const applyModalValue = () => {
      // для связывания значений при изменении масштаба
      startDate.value = modalStartDate.value
      endDate.value = modalEndDate.value

      if (filter.isSetOnlyFullDate && modalStartDate.value && modalEndDate.value) {
        filter.addValue({
          endDate: modalEndDate.value,
          startDate: modalStartDate.value,
        })
      } else if (!modalStartDate.value && !modalEndDate.value) {
        filter.removeValues()
      } else if (!filter.isSetOnlyFullDate) {
        filter.addValue({
          endDate: modalEndDate.value,
          startDate: modalStartDate.value,
        })
      }

      setIsModalOpen(false)
    }

    const cancelModal = () => {
      modalStartDate.value = startDate.value
      modalEndDate.value = endDate.value
    }

    watch(filter, (newFilter) => {
      if (!newFilter.startDate && !newFilter.endDate) {
        startDate.value = newFilter.startDate
        endDate.value = newFilter.endDate

        modalStartDate.value = newFilter.startDate
        modalEndDate.value = newFilter.endDate
      }
    })

    return () => (
      <div class="default-table-header default-table-period-calendar-header">
        <span onClick={() => setIsModalOpen(true)}>
          <PeriodCalendarInput
            isForTable
            end={endDate.value}
            start={startDate.value}
            placeholder={props.column.name}
            right={props.column.right}
            onSetStart={(v) => setValue(v)}
            onSetEnd={(v) => setValue(v, true)}
          />
        </span>

        { props.enableSorting && props.onSort && (
          <HeaderSort column={props.column} onSort={props.onSort} sortField={props.sortField} sortOrder={props.sortOrder}/>
        ) }

        <ModalWindow
          class="ark-ui-table-header-modal"

          ref={modal}
          onClose={() => cancelModal()}
          slots={{
            head: props.column.name,
            body: () => (
              <div class="ark-ui-table-header-modal-body">
                <PeriodCalendar
                  end={modalEndDate.value}
                  start={modalStartDate.value}
                  onSetStart={(v) => setModalValue(v)}
                  onSetEnd={(v) => setModalValue(v, true)}
                />
              </div>
            ),
            footer: () => [
              <DefaultButton label="Отмена" type="secondary" onClick={() => cancelModal()} />,
              <DefaultButton
                label="Применить" onClick={() => applyModalValue()}
                isDisabled={!modalStartDate.value && !modalEndDate.value}
              />,
            ],
          }}
        />
      </div>
    )
  },
})
</script>

<style lang="scss" scoped>
.default-table-period-calendar-header {
  display: flex;
  align-items: center;
  // cursor: pointer;

  > *:first-child {
    width: 100%;
  }

  &.active,
  &:hover {
    & path {
      fill: #585B69;
    }
  }
}
</style>
